import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { QRCodeSVG } from 'qrcode.react';
import { Copy, ExternalLink, Wallet, Download, RefreshCw } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SEPOLIA_HEX = '0xaa36a7';

const card = 'bg-slate-800 border border-slate-700 rounded-2xl p-6';
const input = 'w-full bg-slate-900 border border-slate-600 rounded-lg px-4 py-3 text-white font-mono text-sm focus:border-emerald-500 outline-none';

export const WalletConnectCard = ({ data, token, onChange }) => {
  const [manual, setManual] = useState('');
  const [amount, setAmount] = useState('');
  const [busy, setBusy] = useState('');
  const auth = { headers: { Authorization: `Bearer ${token}` } };

  const run = async (key, fn) => {
    setBusy(key);
    try { await fn(); onChange(); } catch (err) { toast.error(err.response?.data?.detail || err.message || 'Request failed'); }
    finally { setBusy(''); }
  };

  const link = (address, label) => run('link', async () => {
    await axios.post(`${API}/pas-banking/link-wallet`, { address, label }, auth);
    toast.success('External wallet linked');
    setManual('');
  });

  const connectMetaMask = async () => {
    if (!window.ethereum) { toast.error('No browser wallet found. Open this page in MetaMask or Trust Wallet, or paste your address below.'); return; }
    const [account] = await window.ethereum.request({ method: 'eth_requestAccounts' });
    await window.ethereum.request({ method: 'wallet_switchEthereumChain', params: [{ chainId: SEPOLIA_HEX }] }).catch(() => {});
    await window.ethereum.request({
      method: 'wallet_watchAsset',
      params: { type: 'ERC20', options: { address: data.token_contract, symbol: data.token_symbol, decimals: 18 } }
    }).catch(() => {});
    link(account, window.ethereum.isTrust ? 'Trust Wallet' : 'MetaMask');
  };

  const withdraw = (e) => {
    e.preventDefault();
    run('withdraw', async () => {
      const res = await axios.post(`${API}/pas-banking/withdraw`, { beth_amount: parseFloat(amount) }, auth);
      toast.success(res.data.message);
      setAmount('');
    });
  };

  const syncDeposits = () => run('sync', async () => {
    const res = await axios.post(`${API}/pas-banking/sync-deposits`, {}, auth);
    toast.success(res.data.message);
  });

  return (
    <div className={card} data-testid="wallet-connect-card">
      <h3 className="text-lg font-bold text-white mb-1 flex items-center gap-2"><Wallet className="w-5 h-5 text-emerald-400" /> Your on-chain BETH wallet</h3>
      <p className="text-slate-400 text-sm mb-5">Registered Ethereum address on {data.network}. Send {data.token_symbol} here from MetaMask, Trust Wallet or any Ethereum wallet.</p>
      <div className="flex gap-5 items-start mb-6">
        <div className="bg-white p-2 rounded-lg" data-testid="receive-qr"><QRCodeSVG value={data.wallet_address} size={104} /></div>
        <div className="min-w-0 flex-1">
          <code className="block text-emerald-400 text-sm break-all mb-2" data-testid="real-wallet-address">{data.wallet_address}</code>
          <div className="flex flex-wrap gap-2">
            <button onClick={() => { navigator.clipboard.writeText(data.wallet_address); toast.success('Address copied'); }} data-testid="copy-real-address-btn" className="px-3 py-1.5 bg-slate-700 hover:bg-slate-600 text-white rounded text-xs flex items-center gap-1"><Copy className="w-3 h-3" /> Copy</button>
            <a href={data.etherscan_url} target="_blank" rel="noopener noreferrer" data-testid="wallet-etherscan-link" className="px-3 py-1.5 bg-slate-700 hover:bg-slate-600 text-white rounded text-xs flex items-center gap-1">Etherscan <ExternalLink className="w-3 h-3" /></a>
            <button onClick={syncDeposits} disabled={busy === 'sync'} data-testid="sync-deposits-btn" className="px-3 py-1.5 bg-emerald-500/20 text-emerald-300 hover:bg-emerald-500/30 rounded text-xs flex items-center gap-1"><RefreshCw className={`w-3 h-3 ${busy === 'sync' ? 'animate-spin' : ''}`} /> Check for deposits</button>
          </div>
          <p className="text-slate-500 text-xs mt-2">Token contract: <span className="font-mono">{data.token_contract}</span></p>
        </div>
      </div>

      <div className="border-t border-slate-700 pt-5 mb-5">
        <p className="text-sm font-medium text-slate-300 mb-2">Linked external wallet</p>
        {data.linked_wallet ? (
          <p className="text-sm text-white mb-3" data-testid="linked-wallet">{data.linked_wallet.label}: <span className="font-mono text-emerald-400 break-all">{data.linked_wallet.address}</span></p>
        ) : <p className="text-sm text-slate-500 mb-3">None linked yet</p>}
        <div className="flex flex-col sm:flex-row gap-2">
          <button onClick={connectMetaMask} disabled={busy === 'link'} data-testid="connect-metamask-btn" className="px-4 py-3 bg-orange-500 hover:bg-orange-600 text-white rounded-lg text-sm font-medium whitespace-nowrap">Connect MetaMask / Trust</button>
          <input value={manual} onChange={(e) => setManual(e.target.value)} placeholder="Paste 0x address" className={input} data-testid="manual-wallet-input" />
          <button onClick={() => link(manual, 'External wallet')} disabled={!manual || busy === 'link'} data-testid="link-wallet-btn" className="px-4 py-3 bg-slate-700 hover:bg-slate-600 text-white rounded-lg text-sm disabled:opacity-50">Link</button>
        </div>
      </div>

      <form onSubmit={withdraw} className="border-t border-slate-700 pt-5">
        <p className="text-sm font-medium text-slate-300 mb-2">Withdraw to linked wallet (real Sepolia/Mainnet transaction)</p>
        <div className="flex gap-2">
          <input type="number" min="0.01" step="0.01" required value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="BETH amount" className={input} data-testid="withdraw-amount-input" />
          <button type="submit" disabled={!data.linked_wallet || busy === 'withdraw'} data-testid="withdraw-btn" className="px-5 py-3 bg-emerald-500 hover:bg-emerald-600 text-white rounded-lg text-sm font-medium flex items-center gap-1 disabled:opacity-50"><Download className="w-4 h-4" /> {busy === 'withdraw' ? 'Sending...' : 'Withdraw'}</button>
        </div>
        <p className="text-slate-500 text-xs mt-1">= ${(parseFloat(amount || 0) * (data.rate?.usd_per_beth ?? 0.01)).toFixed(2)} USD{data.daily_limit_usd ? ' · counts toward the $250,000 daily limit' : ''}</p>
      </form>
    </div>
  );
};
